import React, {useState, useEffect} from 'react';
import {BrowserRouter as Router, Routes, Route, useLocation} from 'react-router-dom';
import {TelegramProvider} from './components/services/telegramContext';
import ProtectedRoute from './components/services/ProtectedRoute';
import {apiService} from './components/services/api_url';
import Navigation from './components/widgets/Navigation/Navigation';
import {useTelegramContext} from './components/services/telegramContext';
import {useAppStore} from './components/store/useAppStore';

import './App.css';

import MainPage from './components/pages/MainPage/MainPage';
import PaymentPage from './components/pages/PaymentPage/PaymentPage';
import PaymentSuccessPage from './components/pages/PaymentSuccessPage/PaymentSuccessPage';
import PaymentDevicesPage from './components/pages/PaymentDevicesPage/PaymentDevicesPage';
import ProfilePage from './components/pages/ProfilePage/ProfilePage';
import PromoPage from './components/pages/PromoPage/PromoPage';
import SupportPage from './components/pages/SupportPage/SupportPage';
import SupportChatPage from './components/pages/SupportChatPage/SupportChatPage';
import InstructionsPage from './components/pages/InstructionsPage/InstructionsPage';
import DevicesPage from './components/pages/DevicesPage/DevicesPage';
import DeviceDetailPage from './components/pages/DeviceDetailPage/DeviceDetailPage';
import InstallPage from './components/pages/InstallPage/InstallPage';
import KeyAuthPage from './components/pages/KeyAuthPage/KeyAuthPage';
import ConnectPage from './components/pages/ConnectPage/ConnectPage';
import UnauthorizedPage from './components/pages/UnauthorizedPage/UnauthorizedPage';
import ReferralPage from './components/pages/ReferralPage/ReferralPage';
import InstructionDetailPage from './components/pages/InstructionDetailPage/InstructionDetailPage';
import PaymentHistoryPage from './components/pages/PaymentHistoryPage/PaymentHistoryPage';
import FaqPage from './components/pages/FaqPage/FaqPage';
import AddEmailPage from './components/pages/AddEmailPage/AddEmailPage';
import ChangeEmailPage from './components/pages/ChangeEmailPage/ChangeEmailPage';
import OtherPlatformsPage from './components/pages/OtherPlatformsPage/OtherPlatformsPage';
import OtherInstructionDetailPage from './components/pages/OtherInstructionDetailPage/OtherInstructionDetailPage';
import AdminPage from './components/pages/AdminPage/AdminPage';
import IncyRedirectPage from './components/pages/IncyRedirectPage/IncyRedirectPage';
import HappRedirectPage from './components/pages/HappRedirectPage/HappRedirectPage';



interface AppContentProps {
    isPaymentModalOpen: boolean;
    setIsPaymentModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

const AppContent: React.FC<AppContentProps> = ({
                                                   isPaymentModalOpen,
                                                   setIsPaymentModalOpen,
                                               }) => {
    const location = useLocation();

    const {isTelegram, isReady} = useTelegramContext();
    const {
        authChecked,
        setAuthChecked,
        setIsAuthenticated,
        setTelegramId,
        loadUserData
    } = useAppStore();

    const isPublicRoute =
        location.pathname === '/unauthorized' ||
        location.pathname.startsWith('/key/');

    useEffect(() => {
        if (!isReady || isTelegram || isPublicRoute || authChecked) {
            return;
        }

        let cancelled = false;

        const checkSiteAuth = async () => {
            try {
                const authCheck = await apiService.checkAuth();

                if (cancelled) return;

                if (authCheck.status === 'authenticated') {
                    setIsAuthenticated(true);
                    setTelegramId(authCheck.user.telegram_id);
                    await loadUserData(authCheck.user.telegram_id);
                } else {
                    setIsAuthenticated(false);
                }
            } catch (error) {
                console.error('Site auth check error:', error);
                setIsAuthenticated(false);
            } finally {
                if (!cancelled) {
                    setAuthChecked(true);
                }
            }
        };

        checkSiteAuth();

        return () => {
            cancelled = true;
        };
    }, [
        isReady,
        isTelegram,
        isPublicRoute,
        authChecked,
        setAuthChecked,
        setIsAuthenticated,
        setTelegramId,
        loadUserData
    ]);

    if (!isReady) {
        return null;
    }

    if (!isTelegram && !isPublicRoute && !authChecked) {
        return null;
    }

    const hideNavigation =
        location.pathname === '/unauthorized' ||
        location.pathname.startsWith('/key/');

    const showNavigation = !hideNavigation && !isPaymentModalOpen;

    return (
        <div className={`App app-shell ${showNavigation ? 'with-nav' : 'without-nav'}`}>
            {showNavigation && <Navigation/>}

            <main className="app-main">
                <div className={`desktop-page-wrapper ${showNavigation ? 'with-panel' : 'without-panel'}`}>
                    <div className={`desktop-page-panel ${showNavigation ? 'is-visible' : 'is-hidden'}`}>
                        <div className="desktop-page-scroll">
                            <Routes>
                                <Route path="/key/:url_key" element={<KeyAuthPage/>}/>
                                <Route path="/unauthorized" element={<UnauthorizedPage/>}/>

                                <Route
                                    path="/"
                                    element={
                                        <ProtectedRoute>
                                            <MainPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/payment"
                                    element={
                                        <ProtectedRoute>
                                            <PaymentPage onModalStateChange={setIsPaymentModalOpen}/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/payment/success"
                                    element={
                                        <ProtectedRoute>
                                            <PaymentSuccessPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/payment-devices"
                                    element={
                                        <ProtectedRoute>
                                            <PaymentDevicesPage onModalStateChange={setIsPaymentModalOpen}/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/profile"
                                    element={
                                        <ProtectedRoute>
                                            <ProfilePage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/profile/promo"
                                    element={
                                        <ProtectedRoute>
                                            <PromoPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/devices"
                                    element={
                                        <ProtectedRoute>
                                            <DevicesPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/device/:deviceId"
                                    element={
                                        <ProtectedRoute>
                                            <DeviceDetailPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/support"
                                    element={
                                        <ProtectedRoute>
                                            <SupportPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/support-chat"
                                    element={
                                        <ProtectedRoute>
                                            <SupportChatPage onModalStateChange={setIsPaymentModalOpen}/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/instructions"
                                    element={
                                        <ProtectedRoute>
                                            <InstructionsPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/install"
                                    element={
                                        <ProtectedRoute>
                                            <InstallPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/connect"
                                    element={
                                        <ProtectedRoute>
                                            <ConnectPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/referral"
                                    element={
                                        <ProtectedRoute>
                                            <ReferralPage onModalStateChange={setIsPaymentModalOpen}/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/instructions/:platform/:protocol?"
                                    element={
                                        <ProtectedRoute>
                                            <InstructionDetailPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/payment-history"
                                    element={
                                        <ProtectedRoute>
                                            <PaymentHistoryPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/faq"
                                    element={
                                        <ProtectedRoute>
                                            <FaqPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/add-email"
                                    element={
                                        <ProtectedRoute>
                                            <AddEmailPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/change-email"
                                    element={
                                        <ProtectedRoute>
                                            <ChangeEmailPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/other-platforms"
                                    element={
                                        <ProtectedRoute>
                                            <OtherPlatformsPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/other-instructions/:platform"
                                    element={
                                        <ProtectedRoute>
                                            <OtherInstructionDetailPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route
                                    path="/admin"
                                    element={
                                        <ProtectedRoute>
                                            <AdminPage/>
                                        </ProtectedRoute>
                                    }
                                />

                                <Route path="/incy/:key" element={<IncyRedirectPage/>}/>
                                <Route path="/happ/:key" element={<HappRedirectPage />} />

                            </Routes>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

const App: React.FC = () => {
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState<boolean>(false);

    return (
        <TelegramProvider>
            <Router>
                <AppContent
                    isPaymentModalOpen={isPaymentModalOpen}
                    setIsPaymentModalOpen={setIsPaymentModalOpen}
                />
            </Router>
        </TelegramProvider>
    );
};

export default App;